import React from 'react';
import LegalPageLayout from './LegalPageLayout';

const EFFECTIVE_DATE = 'April 24, 2026';

const AutoRajaPrivacy = ({ navigateToPath }) => (
  <LegalPageLayout
    route="autoraja-privacy"
    kicker="Legal"
    title="AutoRaja Privacy Policy"
    navigateToPath={navigateToPath}
  >
    <p className="effective-date">Effective Date: {EFFECTIVE_DATE}</p>

    <p className="legal-lead">
      AutoRaja is published by Mixout Technologies Private Limited. This Privacy
      Policy explains how we may collect, use, disclose, and retain information
      in connection with your use of the AutoRaja mobile game.
    </p>

    <nav className="legal-toc" aria-label="Policy sections">
      <p className="legal-toc-label">On this page</p>
      <ul>
        <li><a href="#information-we-collect">Information We Collect</a></li>
        <li><a href="#how-we-use-information">How We Use Information</a></li>
        <li><a href="#advertising-and-analytics">Advertising and Analytics</a></li>
        <li><a href="#data-sharing">Data Sharing</a></li>
        <li><a href="#data-retention">Data Retention</a></li>
        <li><a href="#security">Security</a></li>
        <li><a href="#childrens-privacy">Children&apos;s Privacy</a></li>
        <li><a href="#your-rights">Your Rights and Choices</a></li>
        <li><a href="#data-deletion">Data Deletion Requests</a></li>
        <li><a href="#changes">Changes to This Policy</a></li>
        <li><a href="#contact">Contact Us</a></li>
      </ul>
    </nav>

    <h2 id="information-we-collect">Information We Collect</h2>
    <p>
      We may collect limited information necessary to operate, improve, and
      support the game. This may include gameplay and usage analytics, crash
      diagnostics, device information, operating system information, app
      version information, advertising identifiers (such as the Apple
      Advertising Identifier on iOS or the Android Advertising ID on Android),
      other advertising-related data, and support communications you send to
      us.
    </p>

    <h2 id="how-we-use-information">How We Use Information</h2>
    <p>
      We may use collected information to operate and maintain the game, improve
      gameplay and performance, diagnose crashes and technical issues, support
      users, monitor abuse or fraud, comply with legal obligations, and manage
      advertising or analytics integrations.
    </p>

    <h2 id="advertising-and-analytics">Advertising and Analytics</h2>
    <p>
      <strong>Advertising.</strong> AutoRaja uses Google AdMob to serve in-app
      advertisements. When AdMob is active, Google may collect and process
      information such as advertising identifiers (the Apple Advertising
      Identifier or the Android Advertising ID), device and operating system
      details, IP address, approximate location derived from IP address, and
      limited interaction data such as ad impressions and clicks. Google&apos;s
      handling of this data is governed by the{' '}
      <a
        href="https://policies.google.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
      >
        Google Privacy Policy
      </a>{' '}
      and{' '}
      <a
        href="https://policies.google.com/technologies/ads"
        target="_blank"
        rel="noopener noreferrer"
      >
        Google&apos;s advertising policies
      </a>
      .
    </p>
    <p>
      In the future, AutoRaja may also use AppLovin MAX as an ad mediation
      platform alongside AdMob. If and when AppLovin MAX is introduced,
      additional mediated ad networks may process similar advertising-related
      data under their own privacy policies, and this policy will be updated
      to reflect those additions before they ship. See the{' '}
      <a
        href="https://www.applovin.com/privacy/"
        target="_blank"
        rel="noopener noreferrer"
      >
        AppLovin Privacy Policy
      </a>{' '}
      for details about AppLovin&apos;s practices.
    </p>
    <p>
      <strong>Analytics and other services.</strong> AutoRaja may also use
      third-party analytics, crash diagnostics, hosting, and infrastructure
      providers. Any such providers process information according to their own
      privacy policies and applicable law, and this policy will be updated to
      name them explicitly if and when they are added to the shipped build.
    </p>

    <h2 id="data-sharing">Data Sharing</h2>
    <p>
      We may share information with service providers that help us operate,
      analyze, support, host, secure, or monetize the game. We may also disclose
      information when required by law or where necessary to protect our rights,
      users, or services.
    </p>

    <h2 id="data-retention">Data Retention</h2>
    <p>
      We retain information only for as long as reasonably necessary for the
      purposes described in this policy, unless a longer retention period is
      required or permitted by law. Some information may be retained for
      security, fraud prevention, legal compliance, dispute resolution, or
      enforcement purposes.
    </p>

    <h2 id="security">Security</h2>
    <p>
      We take reasonable steps intended to protect information under our control.
      However, no method of transmission or storage is completely secure, and we
      cannot guarantee absolute security.
    </p>

    <h2 id="childrens-privacy">Children&apos;s Privacy</h2>
    <p>
      AutoRaja is not intended for children under the age required by applicable
      law for independent use of such services in their jurisdiction. If you
      believe personal information has been provided inappropriately, please
      contact us.
    </p>

    <h2 id="your-rights">Your Rights and Choices</h2>
    <p>
      Depending on your location, you may have rights regarding access to,
      correction of, or deletion of certain personal information. To make a
      request, contact us using the details below.
    </p>

    <h2 id="data-deletion">Data Deletion Requests</h2>
    <p>
      If you would like to request deletion of data associated with your use of
      AutoRaja, please visit our{' '}
      <a
        href="/autoraja/delete-data"
        onClick={(event) => {
          event.preventDefault();
          navigateToPath('/autoraja/delete-data');
        }}
      >
        Data Deletion page
      </a>{' '}
      or contact us at{' '}
      <a href="mailto:contact@mixout.in?subject=AutoRaja%20Data%20Deletion%20Request">
        contact@mixout.in
      </a>
      .
    </p>

    <h2 id="changes">Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. If we make material
      changes, we may revise the effective date above and post the updated
      policy on this page.
    </p>

    <h2 id="contact">Contact Us</h2>
    <address className="legal-contact-block">
      Mixout Technologies Private Limited
      <br />
      <a href="mailto:contact@mixout.in">contact@mixout.in</a>
    </address>
  </LegalPageLayout>
);

export default AutoRajaPrivacy;
